import { computePosition } from '@floating-ui/dom'
import { SuggestionData } from 'ckeditor5-premium-features'
import { SuggestionDataMap } from 'components/WorkspaceEditor/SuggestionStore'
import {
    TrackChangeStatus,
    TrackChangeType,
    TrackedChange,
} from 'models/changes'
import { TRACK_CHANGE_STATUS_VALUES } from '../constants'

const SINGLE_CHANGE_THRESHOLD = 1

/**
 * Options for setUpdatedCardPositions to reduce parameter count.
 */
export interface SetUpdatedCardPositionsOptions {
    flatSuggestionsRef: React.MutableRefObject<SuggestionData[]>
    prevCardBottomRef: React.MutableRefObject<number>
    rafRef: React.MutableRefObject<number | null>
    overrideSuggestions?: Record<string, SuggestionDataMap>
}

const TIME_FORMATTERS = {
    TODAY_TIME_FORMATTER: new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }),
    DATE_TIME_FORMATTER: new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }),
}

/**
 * A utility function to build an array of suggestions from tracked changes.
 * It maps each tracked change to a suggestion data object using the provided function.
 * @param changes
 * @param createSuggestionDataFromTrackChange
 * @param from
 * @returns TrackedChange[]
 */
export const buildSuggestionArray = (
    changes: TrackedChange[],
    toSuggestionData: (value: TrackedChange, from?: string) => SuggestionData,
    from?: string
) => {
    return changes.reduce<
        {
            id: string
            createdAt: number
            suggestion: SuggestionData
        }[]
    >((arr, value) => {
        const suggestionData = toSuggestionData(value, from)
        if (!suggestionData.id) return arr

        arr.push({
            id: suggestionData.id,
            createdAt: suggestionData.createdAt?.getTime?.() ?? 0,
            suggestion: suggestionData,
        })
        return arr
    }, [])
}

/**
 *  Sorts a suggestion array by `createdAt` in descending order.
 * @param suggestionArray
 * @returns Ordered array of suggestions
 */
export const sortSuggestionArray = (
    suggestionArray: {
        id: string
        createdAt: number
        suggestion: SuggestionData
    }[]
) => {
    return suggestionArray.slice().sort((a, b) => b.createdAt - a.createdAt)
}

/**
 * Converts a sorted array into a record of SuggestionDataMap.
 * Each entry in the record contains the suggestion ID and an array of suggestions.
 * @param sortedArray - The sorted array of suggestions.
 * @returns A record where each key is a suggestion ID and the value is an object containing the ID and an array of suggestions.
 */
export const buildSuggestionRecord = (
    sortedArray: { id: string; suggestion: SuggestionData }[]
): Record<string, SuggestionDataMap> => {
    const record: Record<string, SuggestionDataMap> = {}
    for (const item of sortedArray) {
        record[item.id] = {
            id: item.id,
            suggestions: [item.suggestion],
        }
    }
    return record
}

// NOTE: This logic is a workaround for a backend limitation and should be removed once the backend is fixed.
/**
 * Handles non-option group changes by checking if the change type is OPTIONGROUP
 * and if it has a single changed text. If so, it updates the change type and returns it.
 * Otherwise, it returns the change as is.
 * @param change - The tracked change to handle
 * @returns The modified tracked change or the original change
 */
const handleNonOptionGroupChange = (change: TrackedChange): TrackedChange => {
    const isOptionGroup = change.changeType === TrackChangeType.OPTIONGROUP
    const isRelevantStatus =
        change.status === TrackChangeStatus.PENDING ||
        change.status === TrackChangeStatus.APPROVED ||
        change.status === TrackChangeStatus.REJECTED
    const hasSingleChangedText =
        change.trackChangeTypeToChangedTexts?.length === SINGLE_CHANGE_THRESHOLD

    if (!isOptionGroup || !isRelevantStatus || !hasSingleChangedText) {
        return change // early return if not all required conditions met
    }

    const [singleChange] = change.trackChangeTypeToChangedTexts

    return {
        ...change,
        changeType: singleChange.trackChangeType as TrackChangeType,
        contentItemElementId: singleChange.contentItemElementId,
        changedText: singleChange.changedText,
    }
}

/**
 * Helper function to check if two sets are equal
 */
const areSetsEqual = (a: Set<string>, b: Set<string>): boolean => {
    if (a.size !== b.size) return false
    return [...a].every((item) => b.has(item))
}

/**
 * Compares the trackChangeTypeToChangedTexts arrays between existing and incoming items
 * when the status is OPTIONGROUP, and returns the whole element with status changed to pending
 * if there are differences in trackChangeType values only.
 *
 * @param existingItem - The existing TrackedChange item
 * @param incomingItem - The incoming TrackedChange item
 * @returns The incoming item with status set to 'PENDING' if trackChangeType changes detected, or the original incoming item if no changes
 */
export const handleOptionGroupChange = (
    existingItem: Partial<TrackedChange>,
    incomingItem: TrackedChange
): TrackedChange => {
    // Handle cases where one or both arrays are undefined/null
    const existingTexts = existingItem?.trackChangeTypeToChangedTexts || []
    const incomingTexts = incomingItem?.trackChangeTypeToChangedTexts || []

    // Early return for common cases
    // Note: Remove reference comparison as it will always be false for different array objects
    if (!existingTexts.length && !incomingTexts.length) return incomingItem

    let hasTrackChangeTypeChanges = false

    // If lengths differ, there's definitely a change
    if (existingTexts.length !== incomingTexts.length) {
        hasTrackChangeTypeChanges = true
    } else {
        // Create maps using contentItemElementId as key and trackChangeType as value
        const existingMap = new Map<string, string>()
        const incomingMap = new Map<string, string>()

        // Build map for existing items (key: contentItemElementId, value: trackChangeType)
        existingTexts
            .filter(
                (item) => item?.contentItemElementId && item?.trackChangeType
            )
            .forEach((item) => {
                existingMap.set(item.contentItemElementId, item.trackChangeType)
            })

        // Build map for incoming items (key: contentItemElementId, value: trackChangeType)
        incomingTexts
            .filter(
                (item) => item?.contentItemElementId && item?.trackChangeType
            )
            .forEach((item) => {
                incomingMap.set(item.contentItemElementId, item.trackChangeType)
            })

        // Check if any contentItemElementId has a different trackChangeType or new/removed items
        // 1. Check for changed trackChangeTypes for existing items
        for (const [elementId, existingType] of existingMap) {
            const incomingType = incomingMap.get(elementId)
            if (incomingType && incomingType !== existingType) {
                hasTrackChangeTypeChanges = true
                break
            }
        }

        // 2. Check for new or removed items
        if (!hasTrackChangeTypeChanges) {
            const existingIds = new Set(existingMap.keys())
            const incomingIds = new Set(incomingMap.keys())

            // Check for new items
            for (const id of incomingIds) {
                if (!existingIds.has(id)) {
                    hasTrackChangeTypeChanges = true
                    break
                }
            }

            // Check for removed items
            if (!hasTrackChangeTypeChanges) {
                for (const id of existingIds) {
                    if (!incomingIds.has(id)) {
                        hasTrackChangeTypeChanges = true
                        break
                    }
                }
            }
        }
    }

    // Return the incoming item with status set to 'PENDING' if trackChangeType changes detected
    if (hasTrackChangeTypeChanges) {
        return {
            ...incomingItem,
            status: TrackChangeStatus.PENDING,
        }
    }

    // Return the original incoming item if no changes in trackChangeType
    return incomingItem
}

/** Normalizes tracked changes from the server and identifies stale IDs to remove. */
export const normalizeTrackChanges = (
    incoming: TrackedChange[],
    existing: Record<string, TrackedChange>
): {
    trackChangesDataWithId: Record<string, TrackedChange>
    removeTrackChangesId: string[]
} => {
    if (!incoming || !Array.isArray(incoming) || incoming.length === 0) {
        return { trackChangesDataWithId: {}, removeTrackChangesId: [] }
    }
    const trackChangesDataWithId: Record<string, TrackedChange> = {}
    const removeTrackChangesId: string[] = []

    // Build a set of all new IDs and a map of elementId to newId
    for (const change of incoming) {
        const id = change.id

        const isOptionGroup = change.status === TrackChangeStatus.OPTIONGROUP

        // For OPTIONGROUP items, check if trackChangeType has changed even if status is the same
        if (isOptionGroup) {
            // If this item is already PENDING in the store, update its data but keep PENDING status
            // This ensures the UI shows the latest changes while maintaining the PENDING status
            if (existing[id]?.status === TrackChangeStatus.PENDING) {
                // Merge the incoming data with PENDING status
                trackChangesDataWithId[id] = {
                    ...change, // Use all the new data from API (including updated trackChangeTypeToChangedTexts)
                    status: TrackChangeStatus.PENDING, // But keep the PENDING status
                }
                continue
            }

            // Create a proper default object instead of using empty object with type assertion
            const existingChange: Partial<TrackedChange> = existing[id] || {
                id: change.id,
                status: '',
                changeType: change.changeType,
                trackChangeTypeToChangedTexts: [],
            }
            const processedChange = handleOptionGroupChange(
                existingChange,
                change
            )

            // Always include OPTIONGROUP items - they need to be processed even when unchanged
            // This is different from non-OPTIONGROUP items which are skipped when unchanged
            trackChangesDataWithId[id] = processedChange
        } else {
            // skip unchanged for non-OPTIONGROUP items
            if (existing[id]?.status === change.status) continue
            trackChangesDataWithId[id] = handleNonOptionGroupChange(change)
        }
    }

    // Build incoming element IDs set more efficiently
    const incomingElementIds = new Set(
        incoming
            .map((change) => change.id)
            .filter((id): id is string => Boolean(id))
    )

    for (const id of Object.keys(existing)) {
        if (!incomingElementIds.has(id)) {
            removeTrackChangesId.push(id)
        }
    }

    return { trackChangesDataWithId, removeTrackChangesId }
}

/**
 * Utility to cache today's date information to avoid repeated Date operations.
 */
const DateCache = (() => {
    let _cache: {
        date: string
        year: number
        month: number
        day: number
    } | null = null

    return {
        /**
         * Gets today's date information with caching to avoid repeated Date operations
         */
        getTodayInfo: () => {
            const now = new Date()
            const todayString = now.toDateString()

            // Use cache if it's still the same day
            if (_cache?.date === todayString) {
                // Return a copy to prevent external mutation
                return { ..._cache }
            }

            // Update cache
            _cache = {
                date: todayString,
                year: now.getFullYear(),
                month: now.getMonth(),
                day: now.getDate(),
            }

            return { ..._cache }
        },

        resetCache: () => {
            _cache = null
        },
    }
})()

/**
 * Resets the cached date information to ensure fresh calculations
 */
export const resetDateCache = () => {
    DateCache.resetCache()
}

/**
 * takes a timestamp string and formats it to show "Today, HH:MM:SS AM/PM" if the date is today.
 * @param timeStamp - The timestamp string to format
 * @returns Formatted timestamp string or empty string if invalid
 */
export const formatTimeStamp = (timeStamp: string): string => {
    if (!timeStamp?.trim()) return ''

    try {
        const date = new Date(timeStamp)

        if (isNaN(date.getTime())) return timeStamp

        const today = DateCache.getTodayInfo()

        const isToday =
            date.getFullYear() === today.year &&
            date.getMonth() === today.month &&
            date.getDate() === today.day

        const formattedDate = isToday
            ? `Today ${TIME_FORMATTERS.TODAY_TIME_FORMATTER.format(date)}`
            : TIME_FORMATTERS.DATE_TIME_FORMATTER.format(date).replace(',', '')

        return formattedDate
    } catch {
        return typeof timeStamp === 'string'
            ? timeStamp.replace(/,/, '').replace(/:\d{2}\s/, ' ')
            : timeStamp
    }
}

/**
 * Retrieves the corresponding track change data for a suggestion,
 * based on filter criteria and available track changes.
 *
 * @param suggestion - The CKEditor suggestion data
 * @param trackChangesData - Map of track changes keyed by suggestion:status
 * @param isNotDefaultSuggestionCardFilter - Whether a custom filter is applied
 * @param suggestionCardFilter - The current filter value (OPEN/RESOLVED)
 * @returns The matched TrackedChange or null
 */
export const getTrackChangeById = (
    suggestion: SuggestionData,
    trackChangesData: Record<string, TrackedChange>,
    suggestionCardFilter?: string
): TrackedChange | null => {
    const trackChange = trackChangesData[suggestion?.id] || null
    if (!trackChange) return null

    const status = trackChange.status

    if (
        suggestionCardFilter === TRACK_CHANGE_STATUS_VALUES.OPEN &&
        (status === TrackChangeStatus.OPTIONGROUP ||
            status === TrackChangeStatus.PENDING)
    ) {
        return trackChange
    }

    if (
        suggestionCardFilter === TRACK_CHANGE_STATUS_VALUES.RESOLVED &&
        (status === TrackChangeStatus.APPROVED ||
            status === TrackChangeStatus.REJECTED)
    ) {
        return trackChange
    }

    return null
}

/**
 * Updates the positions of suggestion cards based on their target elements.
 * It computes the new positions and applies them with animation.
 *
 * @param flatSuggestionsRef - Ref to the flat list of suggestions
 * @param prevCardBottomRef - Ref to track the bottom position of the last card
 * @param rafRef - Ref to store the requestAnimationFrame ID for cancellation
 * @param overrideSuggestions - Optional override for suggestions to use instead of the flat list
 * @returns A promise that resolves when the positions are updated
 */
export const setUpdatedCardPositions = async ({
    flatSuggestionsRef,
    prevCardBottomRef,
    rafRef,
    overrideSuggestions,
}: SetUpdatedCardPositionsOptions) => {
    prevCardBottomRef.current = 0

    const allSuggestions = overrideSuggestions
        ? Object.values(overrideSuggestions).flatMap(
              (g) => g?.suggestions || []
          )
        : flatSuggestionsRef.current
    const suggestionPositions: {
        id: string
        top: number
        card: HTMLElement
        target: HTMLElement
    }[] = []

    for (const suggestion of allSuggestions) {
        const id = suggestion?.id
        const target = document.querySelector(
            `[data-suggestion='content-suggestion-id-${id}']`
        ) as HTMLElement
        const card = document.querySelector(
            `[data-suggestion-id='${id}']`
        ) as HTMLElement
        if (!target || !card) {
            if (card) {
                card.classList.remove('visible')
                card.style.top = '0px'
            }
            continue
        }

        const { y } = await computePosition(target, card)
        suggestionPositions.push({ id, top: y, card, target })
    }

    // Sort cards vertically
    suggestionPositions.sort((a, b) => a.top - b.top)
    const positions: { id: string; top: number }[] = []

    for (const { id, top, card } of suggestionPositions) {
        const cardHeight = card.offsetHeight
        let adjustedTop = top - 50
        if (adjustedTop < prevCardBottomRef.current) {
            adjustedTop = prevCardBottomRef.current + 8
        }
        prevCardBottomRef.current = adjustedTop + cardHeight
        positions.push({ id, top: adjustedTop })
    }

    // Cancel pending animation
    if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
    }

    // Animate position updates
    rafRef.current = requestAnimationFrame(() => {
        positions.forEach(({ id, top }) => {
            const el = document.querySelector(
                `[data-suggestion-id='${id}']`
            ) as HTMLElement
            if (el) {
                el.style.top = `${top}px`
                el.classList.add('visible')
            }
        })
    })
}

/**
 * Utility to check if a tracked change is MODIFIED or REPLACED.
 * @param trackChange - The tracked change object.
 * @returns true if changeType is MODIFIED or REPLACED, false otherwise.
 */
export const isModifiedOrReplaced = (trackChange?: TrackedChange): boolean => {
    if (!trackChange) return false

    return (
        trackChange?.changeType === TrackChangeType.MODIFIED ||
        trackChange?.changeType === TrackChangeType.REPLACED
    )
}

/**
 * Helper function to get the date of a suggestion.
 * @param {SuggestionData} item - The suggestion data item.
 * @param {boolean} isUnresolved - Flag indicating if the suggestion is unresolved.
 * @returns {string | number | Date | undefined} The date of the suggestion, either
 */
const getDate = (item: SuggestionData, isUnresolved: boolean) =>
    isUnresolved
        ? item?.createdAt
        : item?.attributes?.approvedOrRejectedDateTime

/**
 *
 * @param item
 * @param isUnresolved
 * @returns  {number} The time in milliseconds since the epoch for the suggestion item.
 * If the date is invalid, returns 0.
 */
export const getSuggestionTime = (
    item: SuggestionData,
    isUnresolved: boolean
): number => {
    const dateValue = getDate(item, isUnresolved)
    if (!dateValue) return 0
    const time = new Date(dateValue as string | number | Date).getTime()
    return isNaN(time) ? 0 : time
}
